import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * CanonicalEnforcer
 *
 * Globalt skydd mot duplicate-content-signaler. Mountas en gång i App.tsx
 * och kör vid varje route-byte:
 *
 *  1. Strippar tracking/sort/filter-parametrar från URL i adressfältet
 *     (history.replaceState — ingen omladdning, ingen ny historik-entry).
 *  2. Säkerställer att <link rel="canonical"> alltid pekar på den rena URL:en.
 *
 * Sidor som har egen <Helmet><link rel="canonical" /></Helmet> får ändå
 * sista ordet — Helmet skriver över när komponenten mountas.
 *
 * Vi rör ALDRIG dessa whitelistade params (de är funktionella):
 *   - q (sök)
 *   - handle (produktvariant)
 *   - variant (produktvariant)
 */
/**
 * Tracking/marketing-params som ALDRIG ska finnas i canonical eller adressfält.
 * Allt annat (filter, sort, paginering, sök) lämnas i adressfältet men
 * exkluderas från canonical för att förhindra duplicate-content.
 */
const TRACKING_PARAMS = new Set([
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "yclid",
  "mc_cid", "mc_eid", "ref", "_kx", "_branch_match_id",
  "__lovable_token",
]);

/**
 * Endast dessa params får finnas i canonical (de skapar "äkta" unika sidor).
 * Allt annat (filter, sort, paginering) → canonical pekar på basversionen.
 */
const CANONICAL_KEEP_PARAMS = new Set(["q", "variant"]);

function buildCanonicalSearch(search: string): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const [k, v] of params.entries()) {
    if (CANONICAL_KEEP_PARAMS.has(k.toLowerCase())) kept.set(k, v);
  }
  const s = kept.toString();
  return s ? `?${s}` : "";
}

function stripTrackingFromAddressBar(loc: { pathname: string; search: string; hash: string }): boolean {
  const params = new URLSearchParams(loc.search);
  let changed = false;
  for (const k of Array.from(params.keys())) {
    if (TRACKING_PARAMS.has(k.toLowerCase())) {
      params.delete(k);
      changed = true;
    }
  }
  if (!changed) return false;
  const s = params.toString();
  const cleanSearch = s ? `?${s}` : "";
  window.history.replaceState(window.history.state, "", `${loc.pathname}${cleanSearch}${loc.hash}`);
  return true;
}

function setCanonical(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

export default function CanonicalEnforcer() {
  const location = useLocation();

  useEffect(() => {
    // 1) Lowercase pathname → undvik /Hund vs /hund som duplicates.
    //    Skydda gemener: behåll case för paths som innehåller %-encoding eller redan är gemener.
    const lowerPath = location.pathname.toLowerCase();
    if (lowerPath !== location.pathname) {
      window.history.replaceState(window.history.state, "", `${lowerPath}${location.search}${location.hash}`);
    }

    // 2) Bygg canonical: ren path + endast q/variant-params (filter/sort/page exkluderas).
    const canonicalSearch = buildCanonicalSearch(location.search);
    const canonical = `https://zooi.se${lowerPath}${canonicalSearch}`;
    setCanonical(canonical);

    // 3) Strippa tracking-params (utm/gclid/fbclid …) från adressfältet.
    stripTrackingFromAddressBar({ pathname: lowerPath, search: location.search, hash: location.hash });
  }, [location.pathname, location.search, location.hash]);

  return null;
}
